import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useMotionValue, useSpring } from 'framer-motion';

/**
 * A soft glowing cursor follower. The native cursor stays visible for
 * accessibility; this adds an outline ring + a lagging glow that reacts to
 * interactive elements. Disabled on touch devices and reduced-motion.
 */
const Ring = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9998;
  pointer-events: none;
  border-radius: 50%;
  border: 1.5px solid var(--primary-light);
  mix-blend-mode: ${(p) => (p.$light ? 'normal' : 'screen')};
`;

const Glow = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9997;
  pointer-events: none;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--wash1), transparent 60%);
  opacity: 0.9;
`;

const isTouch = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const CustomCursor = () => {
  const [enabled] = useState(() => !isTouch() && !prefersReduced());
  const [hovering, setHovering] = useState(false);
  const [visible, setVisible] = useState(false);

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const ringX = useSpring(x, { stiffness: 500, damping: 40, mass: 0.4 });
  const ringY = useSpring(y, { stiffness: 500, damping: 40, mass: 0.4 });
  const glowX = useSpring(x, { stiffness: 120, damping: 25, mass: 0.6 });
  const glowY = useSpring(y, { stiffness: 120, damping: 25, mass: 0.6 });

  useEffect(() => {
    if (!enabled) return undefined;

    const interactiveSel = 'a, button, [role="button"], input, textarea, select, label, .clickable';

    const onMove = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      if (!visible) setVisible(true);
      const t = e.target;
      const isInteractive =
        t instanceof Element &&
        (t.closest(interactiveSel) || getComputedStyle(t).cursor === 'pointer');
      setHovering(!!isInteractive);
    };
    const onLeave = () => setVisible(false);

    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
    };
  }, [enabled, visible, x, y]);

  if (!enabled) return null;

  const size = hovering ? 46 : 28;

  return (
    <>
      <Glow
        style={{
          x: glowX,
          y: glowY,
          translateX: '-50%',
          translateY: '-50%',
          opacity: visible ? 0.9 : 0,
        }}
      />
      <Ring
        style={{ x: ringX, y: ringY, translateX: '-50%', translateY: '-50%' }}
        animate={{
          width: size,
          height: size,
          opacity: visible ? (hovering ? 1 : 0.6) : 0,
          borderColor: hovering ? 'var(--secondary)' : 'var(--primary-light)',
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 22 }}
      />
    </>
  );
};

export default CustomCursor;
